/**
 * 结果下载组件
 * 显示完成状态并提供下载按钮
 * v2.5: 新增成本信息显示
 */
import React from 'react';
import { Card, Button, Result, Space, message, Descriptions, Tag, Typography } from 'antd';
import { DownloadOutlined, RedoOutlined, CheckCircleOutlined, EyeOutlined, BarChartOutlined, DollarOutlined } from '@ant-design/icons';
import { downloadFile } from '../services/api';

const { Text } = Typography;

// 汇率常量
const USD_TO_RMB = 7.1;

const ResultDownload = ({ taskId, filename, taskInfo, onDownload, onRestart, onViewExcel, onViewAnalysis }) => {
  // 辅助函数：计算成本差异百分比
  const getCostDiffText = (estimated, actual) => {
    if (!estimated || estimated === 0) return 'N/A';
    const diff = ((actual - estimated) / estimated * 100).toFixed(1);
    return diff > 0 ? `+${diff}%` : `${diff}%`;
  };

  // 辅助函数：根据差异返回颜色
  const getCostDiffColor = (estimated, actual) => {
    if (!estimated || estimated === 0) return 'default';
    const diffPercent = Math.abs((actual - estimated) / estimated * 100);
    if (diffPercent < 5) return 'green';   // 差异<5%：准确
    if (diffPercent < 15) return 'orange'; // 差异5-15%：尚可
    return 'red';                          // 差异>15%：偏差大
  };
  const handleDownload = async () => {
    try {
      message.loading({ content: '正在下载...', key: 'download' });
      await downloadFile(taskId, filename);
      message.success({ content: '下载成功！', key: 'download' });
    } catch (error) {
      console.error('下载失败:', error);
      if (error.response?.status === 401 || error.response?.status === 403) {
        message.error({ content: '请先登录', key: 'download' });
      } else {
        message.error({ content: '下载失败', key: 'download' });
      }
    }
  };

  return (
    <Card
      title={
        <Space>
          <CheckCircleOutlined style={{ color: '#52c41a' }} />
          <span>处理完成</span>
        </Space>
      }
      style={{ marginBottom: 24 }}
    >
      <Result
        status="success"
        title="打标任务已完成！"
        subTitle="您可以在线查看Excel、下载结果文件，或重新开始新的打标任务"
        extra={[
          <Button
            type="primary"
            size="large"
            icon={<EyeOutlined />}
            onClick={() => onViewExcel(taskId, filename)}
            key="view"
          >
            查看 Excel
          </Button>,
          <Button
            size="large"
            icon={<BarChartOutlined />}
            onClick={() => onViewAnalysis(taskId)}
            key="analysis"
          >
            查看数据分析
          </Button>,
          <Button
            size="large"
            icon={<DownloadOutlined />}
            onClick={handleDownload}
            key="download"
          >
            下载结果文件
          </Button>,
          <Button
            size="large"
            icon={<RedoOutlined />}
            onClick={onRestart}
            key="restart"
          >
            重新开始
          </Button>,
        ]}
      />

      {/* v2.5 新增：成本信息显示 */}
      {taskInfo && (taskInfo.estimated_cost > 0 || taskInfo.actual_cost > 0 || taskInfo.estimated_tokens > 0) && (
        <Card
          title={
            <Space>
              <DollarOutlined style={{ color: '#1890ff' }} />
              <span>任务成本信息</span>
            </Space>
          }
          size="small"
          style={{ marginTop: 16, background: '#fafafa' }}
        >
          <Descriptions bordered column={2} size="small">
            {/* 成本对比 */}
            {taskInfo.estimated_cost > 0 && (
              <Descriptions.Item label="预估成本" span={1}>
                <Tag color="blue">¥{(taskInfo.estimated_cost * USD_TO_RMB).toFixed(4)}</Tag>
              </Descriptions.Item>
            )}
            {taskInfo.actual_cost > 0 && (
              <Descriptions.Item label="实际成本" span={1}>
                <Space>
                  <Tag color="green">¥{(taskInfo.actual_cost * USD_TO_RMB).toFixed(4)}</Tag>
                  {taskInfo.estimated_cost > 0 && (
                    <Tag color={getCostDiffColor(taskInfo.estimated_cost, taskInfo.actual_cost)}>
                      {getCostDiffText(taskInfo.estimated_cost, taskInfo.actual_cost)}
                    </Tag>
                  )}
                </Space>
              </Descriptions.Item>
            )}

            {/* Token对比 */}
            {taskInfo.estimated_tokens > 0 && (
              <Descriptions.Item label="预估Token数" span={1}>
                {taskInfo.estimated_tokens.toLocaleString()} tokens
              </Descriptions.Item>
            )}
            {(taskInfo.total_input_tokens > 0 || taskInfo.total_output_tokens > 0) && (
              <Descriptions.Item label="实际Token数" span={1}>
                <div>
                  <div>
                    {((taskInfo.total_input_tokens || 0) + (taskInfo.total_output_tokens || 0)).toLocaleString()} tokens
                  </div>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    输入: {(taskInfo.total_input_tokens || 0).toLocaleString()} |
                    输出: {(taskInfo.total_output_tokens || 0).toLocaleString()}
                  </Text>
                </div>
              </Descriptions.Item>
            )}
          </Descriptions>
        </Card>
      )}
    </Card>
  );
};

export default ResultDownload;

